import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import api from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/common/Card';
import { Badge } from '../components/common/Badge';
import { Button } from '../components/common/Button';
import { Alert } from '../components/common/Alert';
import { LeaveRequestStatus } from '../types';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import Modal from '../components/common/Modal';
import { 
  CalendarDaysIcon, 
  UserIcon, 
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { usePermissions } from '../hooks/usePermissions';
import { useNotification } from '../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';

export const LeaveRequestDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canApproveLeaves, user } = usePermissions();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // Fetch departments data
  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const response = await api.get('/departments');
      return response.data.departments || [];
    }
  });
  
  // Helper function to get department from teams
  const getDepartmentFromTeams = (userObj: any) => {
    if (!userObj?.teams || userObj.teams.length === 0) {
      return null;
    }
    
    // Find department based on team's department_id
    if (departments && userObj.teams[0].department_id) {
      const teamDepartment = departments.find((dept: any) => dept.id === userObj.teams[0].department_id);
      if (teamDepartment) {
        return { id: teamDepartment.id, name: teamDepartment.name };
      }
    }
    
    // Fallback if we can't find the actual department name
    return userObj.teams[0].department_id ? { id: userObj.teams[0].department_id, name: 'Department' } : null;
  };

  // Helper function to get the most relevant department for display
  const getUserDepartment = (userObj: any) => {
    if (!userObj) return null;
    
    // Check for direct department assignment
    if (userObj.department?.name) {
      return userObj.department;
    }
    
    // Check for departments array
    if (userObj.departments && userObj.departments.length > 0) {
      return userObj.departments[0];
    }
    
    // Try to find department through team membership
    return getDepartmentFromTeams(userObj);
  };
  
  const { data: request, isLoading, error } = useQuery({
    queryKey: ['leaveRequest', id],
    queryFn: async () => {
      const response = await api.get(`/leaves/${id}`, {
        params: { include: 'departments,teams' }
      });
      return response.data;
    }
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/leaves/${id}/submit`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveRequest', id] });
      navigate('/leaves');
    }
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/leaves/${id}/cancel`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveRequest', id] });
      navigate('/leaves');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      console.log('Delete mutation - deleting leave request with ID:', id);
      const response = await api.delete(`/leaves/${id}`);
      console.log('Delete mutation - response:', response);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveRequest', id] });
      navigate('/leaves');
    },
    onError: (error: any) => {
      console.error('Delete mutation error:', error);
      console.error('Error response:', error.response);
    }
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/leaves/${id}/approve`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveRequest', id] });
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] });
      showNotification({
        type: 'success',
        title: t('leaveRequest.detail.approveSuccess', 'Request Approved'),
        message: t('leaveRequest.detail.approveSuccessMessage', 'Leave request has been approved.')
      });
      navigate('/leaves');
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: t('leaveRequest.detail.approveError', 'Approval Error'),
        message: error.response?.data?.message || t('leaveRequest.detail.approveErrorMessage', 'Failed to approve request.')
      });
    }
  });

  const rejectMutation = useMutation({
    mutationFn: async (reason?: string) => {
      const response = await api.post(`/leaves/${id}/reject`, {
        comment: reason || '',
        reason: reason || ''
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveRequest', id] });
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] });
      showNotification({
        type: 'success',
        title: t('leaveRequest.detail.rejectSuccess', 'Request Rejected'),
        message: t('leaveRequest.detail.rejectSuccessMessage', 'Leave request has been rejected.')
      });
      navigate('/leaves');
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: t('leaveRequest.detail.rejectError', 'Rejection Error'),
        message: error.response?.data?.message || t('leaveRequest.detail.rejectErrorMessage', 'Failed to reject request.')
      });
    }
  });

  const getStatusBadge = (status: LeaveRequestStatus) => {
    const variants = {
      [LeaveRequestStatus.DRAFT]: 'gray',
      [LeaveRequestStatus.PENDING]: 'warning',
      [LeaveRequestStatus.APPROVED]: 'success',
      [LeaveRequestStatus.REJECTED]: 'error',
      [LeaveRequestStatus.CANCELLED]: 'secondary',
    };
    
    return <Badge variant={variants[status] as any}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'rejected':
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = () => {
    cancelMutation.mutate();
    setCancelDialogOpen(false);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteMutation.mutate();
    setDeleteDialogOpen(false);
  };

  const handleApproveClick = () => {
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = () => {
    approveMutation.mutate();
    setApproveDialogOpen(false);
  };

  const handleRejectClick = () => {
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = () => {
    rejectMutation.mutate(rejectReason);
    setRejectDialogOpen(false);
    setRejectReason('');
  };

  if (isLoading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  if (error || !request) {
    // Show notification about the error
    showNotification({
      type: 'error',
      title: t('errors.loadingError', 'Error Loading Request'),
      message: t('leaveRequest.detail.loadingErrorMessage', 'Failed to load leave request details. Please try again later.')
    });
    
    // Return a more user-friendly empty state
    return (
      <div className="p-6 bg-gray-50 rounded-lg text-center">
        <XCircleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('errors.loadingError', 'Error Loading Request')}</h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('leaveRequest.detail.loadingErrorDesc', 'Unable to load request details. Try refreshing the page.')}
        </p>
        <div className="mt-6">
          <button
            type="button"
            onClick={() => navigate('/leaves')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {t('leaveRequest.detail.backToList')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('leaveRequest.detail.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('leaveRequest.detail.requestId')}: {request.id}
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/leaves')}>
            {t('leaveRequest.detail.backToList')}
          </Button>
          {request.status === LeaveRequestStatus.DRAFT && (
            <>
              <Link to={`/leaves/${id}/edit`}>
                <Button variant="outline">
                  {t('leaveRequest.detail.editRequest')}
                </Button>
              </Link>
              <Button 
                variant="primary" 
                onClick={() => submitMutation.mutate()}
                isLoading={submitMutation.isPending}
                disabled={submitMutation.isPending}
              >
                {t('leaveRequest.detail.submitForApproval')}
              </Button>
              <Button 
                variant="danger" 
                onClick={handleDeleteClick}
                isLoading={deleteMutation.isPending}
                disabled={deleteMutation.isPending}
              >
                {t('leaveRequest.detail.deleteRequest')}
              </Button>
            </>
          )}
          {request.status === LeaveRequestStatus.PENDING && request.user_id === user?.id && (
            <Button 
              variant="danger" 
              onClick={handleCancelClick}
              isLoading={cancelMutation.isPending}
              disabled={cancelMutation.isPending}
            >
              {t('leaveRequest.detail.cancelRequest')}
            </Button>
          )}
          {request.status === LeaveRequestStatus.PENDING && canApproveLeaves && (request.user_id !== user?.id || isAdmin) && (
            <>
              <Button 
                variant="success" 
                onClick={() => setApproveDialogOpen(true)}
                isLoading={approveMutation.isPending}
                disabled={approveMutation.isPending}
              >
                {t('leaveRequest.detail.approve')}
              </Button>
              <Button 
                variant="danger" 
                onClick={() => setRejectDialogOpen(true)}
                isLoading={rejectMutation.isPending}
                disabled={rejectMutation.isPending}
              >
                {t('leaveRequest.detail.reject')}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('leaveRequest.detail.requestInformation')}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('leaveRequest.detail.leaveType')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {request.leaveType?.name || t('leaveRequest.detail.unknown')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('leaveRequest.detail.status')}</dt>
                  <dd className="mt-1">
                    {getStatusBadge(request.status)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('leaveRequest.detail.startDate')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {request.start_date ? format(new Date(request.start_date), 'MMMM dd, yyyy') : 'N/A'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('leaveRequest.detail.endDate')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {request.end_date ? format(new Date(request.end_date), 'MMMM dd, yyyy') : 'N/A'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('leaveRequest.detail.workingDays')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {request.total_days || request.workingDays} {t('leaveRequest.detail.days')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('leaveRequest.detail.createdAt')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {request.created_at ? format(new Date(request.created_at), 'MMMM dd, yyyy HH:mm') : 'N/A'}
                  </dd>
                </div>
                {request.reason && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">{t('leaveRequest.detail.reason')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {request.reason}
                    </dd>
                  </div>
                )}
                {request.handoverNotes && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">{t('leaveRequest.detail.handoverNotes')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {request.handover_notes || request.handoverNotes}
                    </dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>

          {request.approvalSteps && request.approvalSteps.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('leaveRequest.detail.approvalHistory')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flow-root">
                  <ul className="-mb-8">
                    {request.approvalSteps.map((step: any, idx: number) => (
                      <li key={step.id}>
                        <div className="relative pb-8">
                          {idx !== request.approvalSteps.length - 1 && (
                            <span
                              className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                              aria-hidden="true"
                            />
                          )}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className="h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white">
                                {getStatusIcon(step.status)}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1 pt-1.5">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {step.approver?.first_name} {step.approver?.last_name}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {step.comment || step.reason || (
                                    step.status === 'approved' ? t('leaveRequest.detail.approvalHistoryActions.approved', 'Zatwierdzył wniosek') :
                                    step.status === 'rejected' ? t('leaveRequest.detail.approvalHistoryActions.rejected', 'Odrzucił wniosek') :
                                    `${step.status} the request`
                                  )}
                                </p>
                                {step.status === 'rejected' && (step.comment || step.reason) && (
                                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                                    <strong>{t('leaveRequest.detail.approvalHistoryActions.rejectionReason', 'Powód odrzucenia')}:</strong> {step.comment || step.reason}
                                  </div>
                                )}
                              </div>
                              <div className="mt-1 text-sm text-gray-500">
                                {(step.processed_at || step.processedAt) && 
                                  format(new Date(step.processed_at || step.processedAt), 'MMM dd, yyyy HH:mm')
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('leaveRequest.detail.employeeInformation')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {request.user?.first_name} {request.user?.last_name}
                    </p>
                    <p className="text-sm text-gray-500">{request.user?.email}</p>
                  </div>
                </div>
                {/* Department information with improved handling */}
                {getUserDepartment(request.user) && (
                  <div className="flex items-start">
                    <div className="ml-8">
                      <p className="text-sm font-medium text-gray-900">
                        {getUserDepartment(request.user)?.name}
                      </p>
                      <p className="text-sm text-gray-500">{t('leaveRequest.detail.department')}</p>
                    </div>
                  </div>
                )}
                
                {/* Teams information */}
                {request.user?.teams && request.user.teams.length > 0 && (
                  <div className="flex items-start mt-2">
                    <div className="ml-8">
                      <p className="text-sm font-medium text-gray-900 mb-1">{t('leaveRequest.detail.teams')}</p>
                      <div className="flex flex-wrap gap-1">
                        {request.user.teams.map((team: any) => (
                          <span key={team.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {team.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {request.emergencyContact && (
            <Card>
              <CardHeader>
                <CardTitle>{t('leaveRequest.detail.emergencyContact')}</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('leaveRequest.detail.name')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {request.emergencyContact}
                    </dd>
                  </div>
                  {request.emergencyPhone && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('leaveRequest.detail.phone')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {request.emergencyPhone}
                      </dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelConfirm}
        title={t('leaveRequest.detail.cancelDialog.title')}
        message={t('leaveRequest.detail.cancelDialog.message')}
        confirmButtonText={t('leaveRequest.detail.cancelDialog.confirm')}
        variant="warning"
      />

      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('leaveRequest.detail.deleteDialog.title')}
        message={t('leaveRequest.detail.deleteDialog.message')}
        confirmButtonText={t('leaveRequest.detail.deleteDialog.confirm')}
        variant="danger"
      />

      <ConfirmDialog
        isOpen={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApproveConfirm}
        title={t('leaveRequest.detail.approveDialog.title', 'Approve Leave Request')}
        message={t('leaveRequest.detail.approveDialog.message', 'Are you sure you want to approve this leave request?')}
        confirmButtonText={t('leaveRequest.detail.approve')}
        variant="success"
      />

      <Modal
        isOpen={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectReason('');
        }}
        title={t('leaveRequest.detail.rejectDialog.title', 'Odrzuć wniosek urlopowy')}
      >
        <div className="p-6">
          <p className="text-sm text-gray-500 mb-4">
            {t('leaveRequest.detail.rejectDialog.message', 'Czy na pewno chcesz odrzucić ten wniosek urlopowy?')}
          </p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('leaveRequest.detail.rejectDialog.reasonLabel', 'Powód odrzucenia (opcjonalnie)')}
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              rows={3}
              placeholder={t('leaveRequest.detail.rejectDialog.reasonPlaceholder', 'Wprowadź powód odrzucenia wniosku...')}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <Button
              variant="danger"
              onClick={handleRejectConfirm}
              disabled={rejectMutation.isPending}
            >
              {t('leaveRequest.detail.reject', 'Odrzuć')}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectReason('');
              }}
            >
              {t('common.cancel', 'Anuluj')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};